"use client";

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * One choice out of a few, shown side by side on a grey track (`EX-HST02`,
 * `docs/03` §6): the Event Pack's "Online / In a hall", the calculator's
 * "People / Teams of 3", the compare table's two plan pickers on a phone. The
 * look is the contest directory's scope filter, lifted into a primitive so the
 * next toggle does not restate it.
 *
 * A group of buttons, each `aria-pressed`, exactly one pressed; `label` names
 * the group for a screen reader. Buttons stay at least 44 px tall on a touch
 * screen (`docs/03` §12) unless `size="sm"` says the row is a desktop filter.
 *
 * `tone="dark"` (`EX-EDR01`) is the same track on the Judge's near-black: the
 * editor's "Aa" settings, where a white track would be the brightest thing on
 * the screen.
 *
 * `icon` (`EX-SCB03`) draws a mark before the label, and with `iconOnly` in
 * place of it: the label then names the button for a screen reader and in its
 * tooltip, and the button is square (the standings' dark and light switch).
 */
export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
  iconOnly?: boolean;
  /**
   * `QZ-DSH02`: a count in a small chip after the label, primary-tinted on the
   * pressed choice (the Studio list's status tabs, "Live 3"). Part of the
   * button's name, so a screen reader reads "Live 3" too.
   */
  count?: string;
};

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  size = "md",
  tone = "light",
  className,
}: {
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  tone?: "light" | "dark";
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("flex gap-1 rounded-xl p-1", tone === "dark" ? "bg-white/10" : "bg-neutral-100", className)}
    >
      {options.map((option) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            aria-label={option.iconOnly ? option.label : undefined}
            title={option.iconOnly ? option.label : undefined}
            disabled={option.disabled}
            onClick={() => {
              if (!pressed) onChange(option.value);
            }}
            className={cn(
              "inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:shrink-0",
              option.iconOnly ? (size === "sm" ? "w-9 flex-none px-0" : "w-11 flex-none px-0") : "px-3",
              size === "sm" ? "h-9 text-sm" : "h-11 text-[15px]",
              tone === "dark"
                ? pressed
                  ? "bg-white/20 text-white shadow-sm"
                  : "text-white/60 hover:text-white"
                : pressed
                  ? "bg-white text-neutral-900 shadow-sm"
                  : "text-neutral-600 hover:text-neutral-900",
            )}
          >
            {option.icon}
            {option.iconOnly ? null : option.label}
            {/* A space, so the name reads "Live 3", not "Live3"; a flex box draws none. */}
            {option.count !== undefined && !option.iconOnly ? " " : null}
            {option.count !== undefined && !option.iconOnly ? (
              <span
                className={cn(
                  "rounded-full px-1.5 text-[11px] font-semibold leading-[18px]",
                  tone === "dark"
                    ? "bg-white/15 text-white/80"
                    : pressed
                      ? "bg-primary/10 text-primary"
                      : "bg-neutral-200/70 text-neutral-600",
                )}
              >
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
